import { createSlug } from './urlHelpers';

// Helpers for the Share button on the listing card and the listing page.
//
// On a phone the button opens the system share sheet, which is where people
// expect to send a link from. Everywhere else it copies the link and a pop-up
// confirms it. navigator.share also exists in desktop Safari, Edge and Chrome on
// Windows, so its presence alone is not the test: the pointer is.

/**
 * The public URL of a listing, e.g. https://parnexus.com/l/some-title/<uuid>.
 * Always the canonical ListingPage route, never a draft or pending variant.
 */
export const listingShareUrl = (marketplaceRootURL, listing) => {
  const id = listing?.id?.uuid;
  const title = listing?.attributes?.title || '';
  const root = (marketplaceRootURL || window.location.origin).replace(/\/$/, '');
  const slug = createSlug(title) || 'listing';
  return `${root}/l/${slug}/${id}`;
};

/** True on touch-first devices that offer the native share sheet. */
export const canUseNativeShare = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  if (typeof navigator.share !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  } catch (e) {
    return false;
  }
};

// For browsers without the async Clipboard API, or where it is refused
// (non-secure context, permission policy).
const copyWithTextarea = text => {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch (e) {
    return false;
  }
};

/** Copy text to the clipboard. Resolves to whether it worked. */
export const copyToClipboard = text => {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      return navigator.clipboard
        .writeText(text)
        .then(() => true)
        .catch(() => copyWithTextarea(text));
    }
  } catch (e) {
    // Fall through to the textarea.
  }
  return Promise.resolve(copyWithTextarea(text));
};
